/**
 * ClubDashboard — strona startowa zalogowanego członka.
 * Punkty, poziom, postęp do następnego, statystyki paragonów, ostatnia aktywność.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clubApi, TIERS } from './clubApi';
import { useClubAuth } from './ClubApp';

const PROMO_ICON = { drink: '🍹', food: '🍕', event: '🎉', hours: '⏰', general: '🎁' };

const sourceIcons = {
  receipt: '🧾',
  mission_register: '✨',
  mission_first_receipt: '📸',
  manual: '⚙️',
};

const greetingByHour = (h) => {
  if (h < 6) return 'Dobranoc';
  if (h < 12) return 'Dzień dobry';
  if (h < 18) return 'Witaj';
  return 'Dobry wieczór';
};

export default function ClubDashboard() {
  const navigate = useNavigate();
  const auth = useClubAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    clubApi.get('/dashboard')
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Błąd ładowania'));
  }, []);

  if (error) {
    return (
      <div className="club-shell">
        <div className="club-alert club-alert-error">⚠️ {error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="club-shell">
        <div className="club-loading">
          <div className="club-loading-spinner" />
          <div style={{ marginTop: 12 }}>Ładowanie…</div>
        </div>
      </div>
    );
  }

  const { member, points_total, tier, stats, recent_activity } = data;
  const greeting = greetingByHour(new Date().getHours());
  const tierColor = tier.current_tier.color || TIERS[tier.current_tier.name]?.color || '#cd7f32';
  const tierLabel = tier.current_tier.label || TIERS[tier.current_tier.name]?.label || tier.current_tier.name;
  const nextLabel = tier.next_tier ? (tier.next_tier.label || TIERS[tier.next_tier.name]?.label || tier.next_tier.name) : null;

  return (
    <div className="club-shell">
      {/* Brand mini header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: 'Manrope', fontWeight: 800, color: 'var(--club-gold)', letterSpacing: 2, fontSize: 14 }}>
          MR JERRY · CLUB
        </div>
        <button
          onClick={() => navigate('/mrjerryclub/profil')}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)',
            color: 'var(--club-gold)', cursor: 'pointer', fontSize: 14, fontWeight: 800,
          }}
          data-testid="club-dash-avatar"
        >
          {(member.firstName || member.email || '?').charAt(0).toUpperCase()}
        </button>
      </div>

      {/* HERO */}
      <div className="club-hero" data-testid="club-hero">
        <div className="club-hero-greeting">{greeting} 👋</div>
        <div className="club-hero-name">{member.firstName || 'Gościu'}</div>
        <div className="club-points" data-testid="club-points-total">{points_total.toLocaleString('pl-PL')}</div>
        <div className="club-points-label">punktów MR JERRY</div>

        <div className="club-tier-pill" style={{ color: tierColor, borderColor: tierColor }} data-testid="club-tier-pill">
          <span style={{ fontSize: 14 }}>👑</span>
          <span>Poziom: {tierLabel}</span>
        </div>

        {tier.next_tier && (
          <>
            <div className="club-progress">
              <div className="club-progress-fill" style={{ width: `${tier.progress_pct}%` }} data-testid="club-tier-progress" />
            </div>
            <div className="club-progress-text">
              <span>{tier.progress_pct}% do {nextLabel}</span>
              <span>jeszcze {tier.points_to_next.toLocaleString('pl-PL')} pkt</span>
            </div>
          </>
        )}
      </div>

      {/* STATS */}
      <div className="club-stats-grid" data-testid="club-stats">
        <div className="club-stat-tile">
          <div className="club-stat-value">{stats.receipts_approved}</div>
          <div className="club-stat-label">Paragonów</div>
        </div>
        <div className="club-stat-tile">
          <div className="club-stat-value" style={{ color: 'var(--club-warning)' }}>{stats.receipts_pending}</div>
          <div className="club-stat-label">W weryfikacji</div>
        </div>
        <div className="club-stat-tile" onClick={() => navigate('/mrjerryclub/kupony')} style={{ cursor: 'pointer' }}>
          <div className="club-stat-value">{stats.rewards_count}</div>
          <div className="club-stat-label">Aktywne kupony</div>
        </div>
      </div>

      {/* CTA — Add receipt */}
      <button
        className="club-btn club-btn-primary"
        style={{ marginTop: 24, fontSize: 16 }}
        onClick={() => navigate('/mrjerryclub/dodaj-paragon')}
        data-testid="club-cta-add-receipt"
      >
        📸 Dodaj paragon i zgarnij punkty
      </button>

      <button
        className="club-btn club-btn-secondary"
        style={{ marginTop: 10 }}
        onClick={() => navigate('/mrjerryclub/nagrody')}
        data-testid="club-cta-rewards"
      >
        🎁 Zobacz katalog nagród
      </button>

      {/* PROMOCJE — horizontalna karuzela */}
      <PromoCarousel />

      {/* Engagement CTAs */}
      <div className="club-section-title">
        <span>✨</span> Pomóż nam być lepszym
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button
          onClick={() => navigate('/mrjerryclub/ocen')}
          data-testid="club-cta-review"
          style={{
            padding: 14, borderRadius: 14, fontFamily: 'inherit', cursor: 'pointer',
            background: 'rgba(212,175,55,0.06)', border: '1px solid var(--club-border)',
            color: 'var(--club-text)', textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 28 }}>⭐</div>
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>Oceń wizytę</div>
          <div style={{ fontSize: 10, color: 'var(--club-text-muted)', marginTop: 2 }}>Bonus za wartościowe opinie</div>
        </button>
        <button
          onClick={() => navigate('/mrjerryclub/pomysl')}
          data-testid="club-cta-idea"
          style={{
            padding: 14, borderRadius: 14, fontFamily: 'inherit', cursor: 'pointer',
            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.3)',
            color: 'var(--club-text)', textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 28 }}>💡</div>
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>Daj nam pomysł</div>
          <div style={{ fontSize: 10, color: 'var(--club-text-muted)', marginTop: 2 }}>Wdrożone = punkty bonusowe</div>
        </button>
      </div>

      {/* RECENT ACTIVITY */}
      <div className="club-section-title">
        <span>📊</span> Ostatnia aktywność
      </div>
      {recent_activity.length === 0 ? (
        <div className="club-card" style={{ textAlign: 'center', color: 'var(--club-text-muted)' }}>
          Brak aktywności. Dodaj pierwszy paragon!
        </div>
      ) : (
        recent_activity.map((row, i) => (
          <div key={i} className="club-activity-row" data-testid={`club-activity-${i}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 18 }}>{sourceIcons[row.source] || '✨'}</span>
              <div className="club-activity-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.source_label}
              </div>
            </div>
            <div className={`club-activity-amount ${row.amount < 0 ? 'neg' : ''}`}>
              {row.amount > 0 ? '+' : ''}{row.amount} pkt
            </div>
          </div>
        ))
      )}
    </div>
  );
}


// =============== PROMO CAROUSEL ===============
function PromoCarousel() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    clubApi.get('/promotions').then((r) => setItems(r.data.promotions || [])).catch(() => setItems([]));
  }, []);

  if (items === null || items.length === 0) return null;

  return (
    <>
      <div className="club-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🎁 Aktualne promocje</span>
        <button
          onClick={() => navigate('/mrjerryclub/promocje')}
          data-testid="club-promo-see-all"
          style={{ background: 'transparent', border: 'none', color: 'var(--club-gold)', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5 }}
        >Zobacz wszystkie →</button>
      </div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, scrollSnapType: 'x mandatory' }} data-testid="club-promo-carousel">
        {items.slice(0, 5).map((p) => (
          <div
            key={p.id}
            onClick={() => navigate('/mrjerryclub/promocje')}
            data-testid={`club-promo-card-${p.id}`}
            style={{
              flex: '0 0 80%',
              scrollSnapAlign: 'start',
              background: 'var(--club-card)',
              borderRadius: 14,
              border: '1px solid var(--club-border-soft)',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <div style={{
              height: 110,
              background: p.photo ? `url(${p.photo}) center/cover`
                : 'linear-gradient(135deg, rgba(212,175,55,0.20), rgba(212,175,55,0.05))',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44,
            }}>{!p.photo && (PROMO_ICON[p.type] || '🎁')}</div>
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--club-text)', lineHeight: 1.2 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: 'var(--club-text-muted)', marginTop: 4 }}>
                {(p.description || '').slice(0, 60)}{(p.description || '').length > 60 ? '…' : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
